import LockOutlined from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  CircularProgress,
  CssBaseline,
  Fade,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../../Hooks/useUser";
import Copyright from "../Copyright";
import UDCBackground from "../../Images/Universidad-Cartagena.webp";
import logo from "../../Images/logo-unicaragena.webp";

const LoginPage = () => {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(
    Boolean(localStorage.getItem("remember_username"))
  );
  const [showUserError, setShowUserError] = useState(false);
  const [showPassError, setShowPassError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [username, setUsername] = useState(
    localStorage.getItem("username") || ""
  );
  const [password, setPassword] = useState("");

  const { login, hasLoginError, message } = useUser();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (username.trim() === "" || password.trim() === "") {
      setShowUserError(username.trim() === "");
      setShowPassError(password.trim() === "");
      return;
    }
    setIsPending(true);
    const ok = await login({ username, password });
    setIsPending(false);
    if (ok) {
      if (checked) {
        localStorage.setItem("username", username);
      } else {
        localStorage.removeItem("username");
      }
      navigate("/admin/dashboard");
    }
  };

  return (
    <>
      <CssBaseline />
      <Box component="main" sx={{ minHeight: "100vh", display: "flex" }}>
        {/* Panel izquierdo — imagen institucional */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            backgroundImage: `linear-gradient(160deg, rgba(15,36,72,0.85) 0%, rgba(26,60,110,0.70) 100%), url(${UDCBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            alignItems: "center",
            justifyContent: "center",
            p: 6,
            gap: 3,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Universidad de Cartagena"
            sx={{
              width: 200,
              height: "auto",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))",
            }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            color="white"
            textAlign="center"
            sx={{ opacity: 0.9, maxWidth: 360, lineHeight: 1.3 }}
          >
            Sistema de Gestión de Admisiones
          </Typography>
          <Typography
            variant="body2"
            color="rgba(255,255,255,0.65)"
            textAlign="center"
            sx={{ maxWidth: 320 }}
          >
            Universidad de Cartagena — Procesamiento de puntajes ICFES
          </Typography>
        </Box>

        {/* Panel derecho — formulario */}
        <Box
          sx={{
            width: { xs: "100%", md: 480 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 3, sm: 6 },
            py: 6,
            bgcolor: "background.default",
          }}
        >
          <Box sx={{ maxWidth: 380, width: "100%", mx: "auto" }}>
            {/* Ícono de acceso */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <LockOutlined sx={{ color: "#fff", fontSize: 22 }} />
            </Box>

            <Typography variant="h4" fontWeight={700} color="text.primary" mb={0.5}>
              Iniciar sesión
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Ingresa tus credenciales para acceder al sistema.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                required
                fullWidth
                id="username"
                label="Usuario"
                name="username"
                autoComplete="username"
                autoFocus
                size="small"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (showUserError) setShowUserError(false);
                }}
                error={showUserError && username.trim().length === 0}
                helperText={
                  showUserError && username.trim().length === 0
                    ? "Usuario requerido"
                    : ""
                }
              />

              <TextField
                required
                fullWidth
                name="password"
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                id="password"
                size="small"
                value={password}
                autoComplete="current-password"
                error={showPassError && password.trim().length === 0}
                helperText={
                  showPassError && password.trim().length === 0
                    ? "Contraseña requerida"
                    : ""
                }
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (showPassError) setShowPassError(false);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => setChecked((prev) => !prev)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Recordar usuario
                  </Typography>
                }
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disableElevation
                disabled={isPending}
                sx={{ mt: 1, py: 1.25 }}
              >
                {isPending ? <CircularProgress size={22} color="inherit" /> : "Ingresar"}
              </Button>

              <Fade in={hasLoginError} unmountOnExit>
                <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }} role="alert">
                  {message}
                </Alert>
              </Fade>
            </Box>

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Copyright />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;
